import * as React from "react";
import { ToastContainer, ToastProps } from "@/components/ui/toast";

type ToastVariant = "default" | "destructive" | "success";

interface ToastOptions {
  title?: string;
  description: string;
  variant?: ToastVariant;
  duration?: number;
}

interface ToastContextType {
  showToast: (options: ToastOptions) => void;
  success: (description: string, title?: string) => void;
  error: (description: string, title?: string) => void;
  info: (description: string, title?: string) => void;
}

const ToastContext = React.createContext<ToastContextType | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastProps[]>([]);

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = React.useCallback(
    ({ title, description, variant = "default", duration = 5000 }: ToastOptions) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const newToast: ToastProps = {
        id,
        title,
        description,
        variant,
        duration,
        onClose: () => removeToast(id),
      };

      setToasts((prev) => [...prev, newToast]);

      // Auto-remove after duration
      setTimeout(() => {
        removeToast(id);
      }, duration);
    },
    [removeToast]
  );

  const success = React.useCallback(
    (description: string, title?: string) => {
      showToast({ description, title, variant: "success" });
    },
    [showToast]
  );

  const error = React.useCallback(
    (description: string, title?: string) => {
      showToast({ description, title, variant: "destructive" });
    },
    [showToast]
  );

  const info = React.useCallback(
    (description: string, title?: string) => {
      showToast({ description, title, variant: "default" });
    },
    [showToast]
  );

  const contextValue = React.useMemo(
    () => ({ showToast, success, error, info }),
    [showToast, success, error, info]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
